import { useAppDispatch, useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { ToastSample } from 'components/common/Toast/models/toast';
import ToastComponent from 'components/common/Toast/page';
import { schemaActions, selectDeliveryId } from 'features/common/schemaSlice';
import { selectDeliveryIDProgress, selectWorkerStatus, workerActions } from 'features/common/workerSlice';
import service from 'features/Delivery/deliveryService';
import {
  deliveryListActions,
  selectDeliveryApprovalStatusMap,
  selectDeliveryDeliveryStatusMap,
  selectDeliverySortBy,
  selectDeliverySortOrder,
} from 'features/Delivery/deliverySlice';
import { Delivery } from 'features/Delivery/models/delivery';
import { checkIsDeliveryFailedOrUnsent } from 'features/Delivery/utils';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { DATE_FORMAT_YYYY_MM_DD } from 'utils';
import {
  APPROVAL_STATUS_APPROVED,
  APPROVAL_STATUS_REJECTED,
  REEDIT_STATUS_HAVE_NO_SCHEMA,
  REEDIT_STATUS_SCHEMA_CREATING,
  REEDIT_STATUS_SCHEMA_READY,
  STATUS_RESPONSE_COMPLETED,
  STATUS_RESPONSE_FAILED,
  STATUS_RESPONSE_PROGRESSING,
  STATUS_RESPONSE_SUCCESS,
} from '../../constants';
export interface SearchDeliveryResultProps {
  data: Delivery[];
  handleDisplayDeliveryDetailModal: (delivery: Delivery) => void;
  handleSelectDelivery: (deliveryId: number) => void;
  handleDeselectDelivery: (deliveryId: number) => void;
  selectedDeliveryIds: number[];
}
const SearchDeliveryResult = ({
  handleDisplayDeliveryDetailModal,
  handleSelectDelivery,
  handleDeselectDelivery,
  data,
  selectedDeliveryIds,
}: SearchDeliveryResultProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedSecondsRef = useRef<number>(0);
  const [currentElapsedSeconds, setCurrentElapsedSeconds] = useState(0);

  // selector
  const deliveryStatusMap = useAppSelector(selectDeliveryDeliveryStatusMap);
  const approvalStatusMap = useAppSelector(selectDeliveryApprovalStatusMap);
  const filterSortBy = useAppSelector(selectDeliverySortBy);
  const filterSortOrder = useAppSelector(selectDeliverySortOrder);
  const workerStatus = useAppSelector(selectWorkerStatus);
  const deliveryIdProgress = useAppSelector(selectDeliveryIDProgress);
  const reEditingByDeliveryId = useAppSelector(selectDeliveryId);

  // Check if any item is currently being processed (schema creating)
  const isAnyItemProcessing = data.some((item) => item.reedit_status === REEDIT_STATUS_SCHEMA_CREATING);

  const handleShowDeliveryDetail = (delivery: Delivery) => {
    if (delivery) {
      handleDisplayDeliveryDetailModal(delivery);
    }
  };

  // Helper function to check if edit button should be shown
  const shouldShowEditButton = (item: Delivery) => {
    // Check if approval status is "APPROVED" (承認済み)
    // Also check if it might be in Japanese or other formats
    const isApproved =
      item.approval_status === APPROVAL_STATUS_APPROVED || item.approval_status === APPROVAL_STATUS_REJECTED;
    if (!isApproved) {
      return false;
    }

    // Check if delivery_date is within 14 days from today
    if (!item.delivery_date) {
      return false;
    }

    // Parse delivery_date string (format: YYYY-MM-DD) and normalize to start of day
    // Use strict parsing to ensure correct date interpretation
    const deliveryDate = moment(item.delivery_date, 'YYYY-MM-DD', true).startOf('day');
    const today = moment().startOf('day');

    // Validate that the date was parsed correctly
    if (!deliveryDate.isValid()) {
      return false;
    }

    const diffInDays = deliveryDate.diff(today, 'days');
    const absDiffInDays = Math.abs(diffInDays);

    // Show button if delivery date is within 14 days from today (past or future)
    const shouldShow = absDiffInDays <= 14;
    return shouldShow;
  };

  function updateFilterSort(sortName: string) {
    let newSortBy = filterSortBy;
    let newSortOrder = filterSortOrder;

    if (newSortBy === sortName) {
      newSortOrder = newSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newSortBy = sortName;
      newSortOrder = 'asc';
    }

    dispatch(deliveryListActions.updateFilterSort({ sortBy: newSortBy, sortOrder: newSortOrder }));
  }

  // check DB version of delivery before re-editing
  const checkDBVersion = async (deliveryId: number): Promise<boolean | undefined> => {
    try {
      const response = await service.checkDBVersion(deliveryId);
      if (response.status === STATUS_RESPONSE_SUCCESS && response.data) {
        if (!response.data.is_correct_db_version) {
          return false;
        }
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  // Check if schema is available (async)
  async function checkSchemaAvailable(deliveryId: number): Promise<boolean | undefined> {
    try {
      const response = await service.checkSchemaAlreadyExists(deliveryId);
      if (
        response.status === STATUS_RESPONSE_SUCCESS &&
        response.data &&
        typeof response.data.schema_exists === 'boolean'
      ) {
        return response.data.schema_exists;
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }
  async function processRestoreSchema(deliveryId: number) {
    // Reset elapsed seconds counter
    elapsedSecondsRef.current = 0;
    setCurrentElapsedSeconds(0);

    // Set worker state to progressing immediately for instant UI feedback
    dispatch(workerActions.setWorkerProgressing({ delivery_id: deliveryId }));

    // Update delivery reedit_status to CREATING immediately for instant UI feedback
    dispatch(
      deliveryListActions.updateDeliveryReeditStatus({
        deliveryId: deliveryId,
        reeditStatus: REEDIT_STATUS_SCHEMA_CREATING,
      })
    );

    try {
      // Call API to start restore process
      const response = await service.asyncRestoreSchema(deliveryId);

      if (response.status === STATUS_RESPONSE_SUCCESS) {
        // Store task metadata without overwriting 'progressing' status
        // so the timer keeps counting seconds
        dispatch(workerActions.setWorkerTaskInfo(response));
        // Start checking status by delivery_id
        checkWorkerStatusByDelivery(deliveryId);
      } else {
        // Handle error case
        dispatch(workerActions.setWorkerFailed());
        // Reset delivery status on error
        dispatch(
          deliveryListActions.updateDeliveryReeditStatus({
            deliveryId: deliveryId,
            reeditStatus: REEDIT_STATUS_HAVE_NO_SCHEMA,
          })
        );
        alert('Failed to initiate schema restoration. Please try again.');
      }
    } catch (error) {
      // Handle exception
      dispatch(workerActions.setWorkerFailed());
      // Reset delivery status on error
      dispatch(
        deliveryListActions.updateDeliveryReeditStatus({
          deliveryId: deliveryId,
          reeditStatus: REEDIT_STATUS_HAVE_NO_SCHEMA,
        })
      );
      alert('Failed to initiate schema restoration. Please try again.');
    }
  }

  // Function to check worker status by delivery_id
  const checkWorkerStatusByDelivery = useCallback(
    async (deliveryId: number) => {
      try {
        const response = await service.getAsyncRestoreStatusByDelivery(deliveryId);
        if (response.status === STATUS_RESPONSE_SUCCESS && response.data) {
          const { status } = response.data;
          const { delivery_id } = response.data;

          // Only proceed if status is 'failed' or 'success'
          if (status === STATUS_RESPONSE_FAILED || status === STATUS_RESPONSE_COMPLETED) {
            dispatch(workerActions.setWorkerSuccess(response));
            if (status === STATUS_RESPONSE_COMPLETED && delivery_id) {
              dispatch(schemaActions.setSchema(delivery_id));
              // Reset worker state after successful completion
              dispatch(workerActions.resetWorker());
              // Update reedit_status to READY only after schema is actually completed
              // This avoids filter state mismatch and shows correct button state
              dispatch(
                deliveryListActions.updateDeliveryReeditStatus({
                  deliveryId: delivery_id,
                  reeditStatus: REEDIT_STATUS_SCHEMA_READY,
                })
              );
              // Reset elapsed seconds after completion
              elapsedSecondsRef.current = 0;
              setCurrentElapsedSeconds(0);
            }
            if (status === STATUS_RESPONSE_FAILED && delivery_id) {
              const msgError = response.data.error || t('App.error');
              const toastList = [ToastSample.toastError(t('App.error'), msgError ?? t('App.error'))];
              showGlobalModal(() => (
                <ToastComponent
                  toastList={toastList}
                  position={'bottom-end'}
                  autoDelete={true}
                  autoDeleteTime={30000}
                />
              ));
              // Reset elapsed seconds after failure
              elapsedSecondsRef.current = 0;
              setCurrentElapsedSeconds(0);
            }

            // Clear the interval since we got a final status
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
          // For other statuses (like 'running' or 'pending'), continue polling
        }
      } catch (error) {
        dispatch(workerActions.setWorkerFailed());

        // Clear the interval on error
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // Timer effect to increment elapsed seconds when worker is active
  useEffect(() => {
    if (workerStatus === STATUS_RESPONSE_PROGRESSING) {
      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Start timer to increment elapsed seconds every second
      timerRef.current = setInterval(() => {
        elapsedSecondsRef.current += 1;
        setCurrentElapsedSeconds(elapsedSecondsRef.current);
        // Update the store with the new elapsed seconds using ref to avoid stale closures
        dispatch(workerActions.updateElapsedSeconds(elapsedSecondsRef.current));
      }, 1000);
    } else {
      // Clear timer when worker is not active
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [workerStatus, dispatch]);

  // ===== FIX: Refresh stale reedit_status when data becomes available =====
  // When browser is closed during processing and reopened later,
  // the Redux state may still have reedit_status=1 (CREATING) even though
  // the backend has already completed and set reedit_status=2 (READY).
  // This effect checks and refreshes the status from the backend.
  const hasRefreshedRef = useRef<boolean>(false);

  useEffect(() => {
    const refreshStaleStatuses = async () => {
      // Find items that appear to be "processing" (reedit_status=1)
      const processingItems = data.filter(
        (item) => item.reedit_status === REEDIT_STATUS_SCHEMA_CREATING
      );

      if (processingItems.length === 0) return;

      // Check all items in PARALLEL using Promise.all (much faster than sequential for loop)
      const checkStatusPromises = processingItems.map(async (item) => {
        try {
          const response = await service.getAsyncRestoreStatusByDelivery(item.id);
          if (response.status === STATUS_RESPONSE_SUCCESS && response.data) {
            const { status, delivery_id } = response.data;

            // If backend says completed, update Redux to reflect the real status
            if (status === STATUS_RESPONSE_COMPLETED && delivery_id) {
              dispatch(
                deliveryListActions.updateDeliveryReeditStatus({
                  deliveryId: delivery_id,
                  reeditStatus: REEDIT_STATUS_SCHEMA_READY,
                })
              );
            }
            // If backend says failed or no active task, reset to no schema
            if (status === STATUS_RESPONSE_FAILED) {
              dispatch(
                deliveryListActions.updateDeliveryReeditStatus({
                  deliveryId: item.id,
                  reeditStatus: REEDIT_STATUS_HAVE_NO_SCHEMA,
                })
              );
            }
          } else {
            // If we can't get status (maybe task was cleaned up), reset to no schema
            // This handles the case where server restarted and lost the task
            dispatch(
              deliveryListActions.updateDeliveryReeditStatus({
                deliveryId: item.id,
                reeditStatus: REEDIT_STATUS_HAVE_NO_SCHEMA,
              })
            );
          }
        } catch (error) {
          // On error, reset to no schema to allow retry
          dispatch(
            deliveryListActions.updateDeliveryReeditStatus({
              deliveryId: item.id,
              reeditStatus: REEDIT_STATUS_HAVE_NO_SCHEMA,
            })
          );
        }
      });

      // Wait for all checks to complete in parallel
      await Promise.all(checkStatusPromises);
    };

    // Run when data first becomes available (only once per component lifecycle)
    if (data.length > 0 && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      refreshStaleStatuses();
    }
  }, [data.length, dispatch]); // Trigger when data length changes
  // ===== END FIX =====

  useEffect(() => {
    // Start polling if we have a delivery_id and status is not final
    if (
      deliveryIdProgress &&
      workerStatus &&
      workerStatus !== STATUS_RESPONSE_FAILED &&
      workerStatus !== STATUS_RESPONSE_SUCCESS
    ) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Get polling interval from environment variable with fallback to 30 seconds
      const pollingInterval = parseInt(process.env.REACT_APP_WORKER_STATUS_POLLING_INTERVAL || '30000', 10);

      // Start polling every configured interval
      intervalRef.current = setInterval(() => {
        checkWorkerStatusByDelivery(deliveryIdProgress);
      }, pollingInterval);
    }

    // Cleanup function to reset worker state when component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [deliveryIdProgress, workerStatus, checkWorkerStatusByDelivery]);

  return (
    <div className="sticky-table flex-grow-1 flex-shrink-1">
      <Table hover>
        <thead style={{ backgroundColor: '#E1D5E7' }}>
          <tr>
            <th style={{ width: '50px' }}>{t('DeliveryFeature.Table.selection')}</th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'id')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.distribution_no')}</span>
                {filterSortBy === 'id' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'id' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'id' && <FaSort />}
              </div>
            </th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'unit')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.distribution_unit')}</span>
                {filterSortBy === 'unit' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'unit' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'unit' && <FaSort />}
              </div>
            </th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'sale_target')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.sales_target')}</span>
                {filterSortBy === 'sale_target' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'sale_target' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'sale_target' && <FaSort />}
              </div>
            </th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'delivery_date')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.scheduled_distribution_date')}</span>
                {filterSortBy === 'delivery_date' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'delivery_date' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'delivery_date' && <FaSort />}
              </div>
            </th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'content')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.delivery_content')}</span>
                {filterSortBy === 'content' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'content' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'content' && <FaSort />}
              </div>
            </th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'delivery_status')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.distribution_status')}</span>
                {filterSortBy === 'delivery_status' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'delivery_status' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'delivery_status' && <FaSort />}
              </div>
            </th>
            <th className="cursor-pointer" onClick={updateFilterSort.bind(null, 'approval_status')}>
              <div className="d-flex align-items-center">
                <span className="flex-fill">{t('DeliveryFeature.Table.approval_state')}</span>
                {filterSortBy === 'approval_status' && filterSortOrder === 'asc' && <FaSortUp />}
                {filterSortBy === 'approval_status' && filterSortOrder === 'desc' && <FaSortDown />}
                {filterSortBy !== 'approval_status' && <FaSort />}
              </div>
            </th>
            <th>{t('DeliveryFeature.Table.applicant')}</th>
            <th>{t('DeliveryFeature.Table.application_date')}</th>
            <th>{t('DeliveryFeature.Table.confirmer')}</th>
            <th>{t('DeliveryFeature.Table.date_confirmed')}</th>
            <th>{t('DeliveryFeature.Table.recognized')}</th>
            <th>{t('DeliveryFeature.Table.approval_date')}</th>
            <th>{t('DeliveryFeature.Table.edit')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const isDeliveryFailedOrUnsent = checkIsDeliveryFailedOrUnsent(item.delivery_status);
            return (
              <tr className="text-center" key={item.id}>
                <td>
                  <Form.Check
                    className="custom-checkbox"
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSelectDelivery(item.id);
                      } else {
                        handleDeselectDelivery(item.id);
                      }
                    }}
                    checked={selectedDeliveryIds.includes(item.id)}
                  />
                </td>
                <td>
                  <button
                    onClick={() => {
                      item && handleShowDeliveryDetail(item);
                    }}
                    type="button"
                    className="btn btn-link"
                  >
                    {item.id}
                  </button>
                </td>
                <td>{item.unit}</td>
                <td>{item.sale_target}</td>
                <td>{item.delivery_date && moment(item.delivery_date).format(DATE_FORMAT_YYYY_MM_DD)}</td>
                <td title={item.content}>
                  <label style={{ maxWidth: '100px' }} className="text-single-row" title={item.content}>
                    {item.content}
                  </label>
                </td>
                <td className={isDeliveryFailedOrUnsent ? 'cell-warning' : ''}>
                  {deliveryStatusMap[item.delivery_status] || '-'}
                </td>
                <td>{approvalStatusMap[item.approval_status] || '-'}</td>
                <td>{item.applicant_name}</td>
                <td>{item.apply_date && moment(item.apply_date).format(DATE_FORMAT_YYYY_MM_DD)}</td>
                <td>{item.confirmor_name}</td>
                <td>{item.confirm_date && moment(item.confirm_date).format(DATE_FORMAT_YYYY_MM_DD)}</td>
                <td>{item.approver_name}</td>
                <td>{item.approver_date && moment(item.approver_date).format(DATE_FORMAT_YYYY_MM_DD)}</td>
                <td>
                  {(() => {
                    const showRedButton =
                      (workerStatus === STATUS_RESPONSE_COMPLETED &&
                        reEditingByDeliveryId === item.id &&
                        item.reedit_status === REEDIT_STATUS_SCHEMA_READY) ||
                      (item.reedit_status === REEDIT_STATUS_SCHEMA_READY &&
                        (item.approval_status === APPROVAL_STATUS_APPROVED ||
                          item.approval_status === APPROVAL_STATUS_REJECTED));

                    return showRedButton ? (
                      <button
                        type="button"
                        disabled={workerStatus === STATUS_RESPONSE_PROGRESSING || isAnyItemProcessing}
                        className="btn btn--dark-green btn-sm"
                        style={{ backgroundColor: '#D80073', borderColor: '#D80073' }}
                        onClick={() => {
                          dispatch(schemaActions.setSchema(item.id));
                          history.push(`/menu-book/menu-book-list?delivery_id=${item.id}&edit_mode=reedit&`);
                        }}
                      >
                        {t('DeliveryFeature.Text.editing')}
                      </button>
                    ) : shouldShowEditButton(item) ? (
                      <button
                        type="button"
                        disabled={workerStatus === STATUS_RESPONSE_PROGRESSING || isAnyItemProcessing}
                        className="btn btn-primary btn-sm"
                        onClick={async () => {
                          const isValidDBVersion = await checkDBVersion(item.id);
                          if (isValidDBVersion === false) {
                            showGlobalModal(() => (
                              <AlertModal
                                title={t('App.info')}
                                message={t('DeliveryFeature.Alert.db_version_mismatch')}
                              />
                            ));
                            return;
                          }
                          const statusSchemaExists = await checkSchemaAvailable(item.id);
                          if (
                            statusSchemaExists === false ||
                            statusSchemaExists === undefined ||
                            item.reedit_status === REEDIT_STATUS_SCHEMA_CREATING
                          ) {
                            // Show confirmation popup before restoring schema
                            showGlobalModal(() => (
                              <ConfirmModal
                                title={t('App.confirm')}
                                message={t('DeliveryFeature.Confirm.restore_schema')}
                                onConfirm={async () => {
                                  item && (await processRestoreSchema(item.id));
                                }}
                              />
                            ));
                          } else {
                            dispatch(schemaActions.setSchema(item.id));
                            history.push(`/menu-book/menu-book-list?delivery_id=${item.id}&edit_mode=reedit&`);
                          }
                        }}
                      >
                        {workerStatus === STATUS_RESPONSE_PROGRESSING && deliveryIdProgress === item.id
                          ? `処理中  (${currentElapsedSeconds}秒)`
                          : item.reedit_status === REEDIT_STATUS_SCHEMA_CREATING
                            ? '処理中'
                            : t('DeliveryFeature.Text.edit')}
                      </button>
                    ) : null;
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
export default SearchDeliveryResult;
